import {defaultFilterProviderOptions} from '../constants/defaults'
import {FILTER_KEYS, FILTER_VALUES} from '../constants/filter-constants'
import {Strings} from '../constants/strings'
import type {FilterQuery} from '../filter-query'
import {
  type FilterBlock,
  type FilterConfig,
  type FilterKey,
  FilterOperator,
  FilterProviderType,
  type FilterSuggestion,
  type IndexedBlockValueItem,
  type SuppliedFilterProviderOptions,
  ValidationTypes,
} from '../types'
import {getFilterValue, getUnescapedFilterValue} from '../utils'
import {
  getEmptyValueValidation,
  getInvalidValueValidation,
  getMultiValueValidation,
  StaticFilterProvider,
} from './static'

const DATE_REGEX = /^(?:>|<|>=|<=)?(\d{4}-\d{1,2}-\d{1,2})?$/
const DATE_YEAR_MONTH_REGEX = /^(?:>|<|>=|<=)?(\d{4}-\d{1,2})?$/
const DATE_YEAR_REGEX = /^(?:>|<|>=|<=)?(\d{4})?$/
const DATE_TIME_REGEX = /^(?:>|<|>=|<=)?(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z?)$/
const DATE_TIME_TZ_REGEX = /^(?:>|<|>=|<=)?(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\+\d{2}:\d{2})$/
const DYNAMIC_DATE_REGEX = /^(?:>|<|>=|<=)?(@[A-Za-z]+)([+-]\d+[mdwqy])?$/
const operators = ['>=', '<=', '>', '<']

export class DateFilterProvider extends StaticFilterProvider {
  constructor(filterKey: FilterKey, filterValues: FilterSuggestion[], options?: SuppliedFilterProviderOptions) {
    super(filterKey, filterValues, {
      ...defaultFilterProviderOptions,
      ...options,
      filterTypes: {
        ...defaultFilterProviderOptions.filterTypes,
        multiKey: false,
        multiValue: false,
        ...options?.filterTypes,
      },
    })
    this.type = FilterProviderType.Date
    this.filterValues = filterValues.map(fv => ({...fv, ariaLabel: `${fv.displayName}, ${this.displayName}`}))
  }

  override validateFilterBlockValues(
    filterQuery: FilterQuery,
    block: FilterBlock,
    values: IndexedBlockValueItem[],
    _: FilterConfig,
  ): IndexedBlockValueItem[] {
    return values.map((v, i) => {
      if (!this.allowsFilterValues()) {
        return {
          ...v,
          valid: false,
          validations: [{type: ValidationTypes.InvalidValue, message: Strings.filterValuesUnsupported(this.key)}],
        }
      }

      if (
        !this.options.filterTypes.multiValue &&
        ((block.operator === FilterOperator.Between && i > 1) || (block.operator !== FilterOperator.Between && i > 0))
      ) {
        return getMultiValueValidation(v, this.key)
      }

      const extractedValue = getUnescapedFilterValue(v.value)

      if (!extractedValue) return getEmptyValueValidation(v, this.key)

      if (block.operator === FilterOperator.Between && operators.some(op => extractedValue.startsWith(op))) {
        return getInvalidValueValidation(v, extractedValue, this.key)
      }

      let typeValidity = true

      if (extractedValue) {
        if (extractedValue === '*') {
          typeValidity = true
        } else if (DYNAMIC_DATE_REGEX.test(extractedValue)) {
          const [, date] = DYNAMIC_DATE_REGEX.exec(extractedValue) ?? []
          typeValidity = this.filterValues.find(fv => getFilterValue(fv.value) === date) !== undefined
        } else {
          let date = null
          if (DATE_TIME_TZ_REGEX.test(extractedValue)) {
            date = DATE_TIME_TZ_REGEX.exec(extractedValue)?.[1] ?? null
          } else if (DATE_TIME_REGEX.test(extractedValue)) {
            date = DATE_TIME_REGEX.exec(extractedValue)?.[1] ?? null
          } else if (DATE_REGEX.test(extractedValue)) {
            date = DATE_REGEX.exec(extractedValue)?.[1] ?? null
          } else if (DATE_YEAR_MONTH_REGEX.test(extractedValue)) {
            date = DATE_YEAR_MONTH_REGEX.exec(extractedValue)?.[1] ?? null
          } else if (DATE_YEAR_REGEX.test(extractedValue)) {
            date = DATE_YEAR_REGEX.exec(extractedValue)?.[1] ?? null
          }
          typeValidity = date ? !isNaN(Date.parse(date)) : false
        }
      }
      return {...v, valid: typeValidity}
    })
  }
}

export class ClosedFilterProvider extends DateFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.closed, FILTER_VALUES.closed, options)
    this.type = FilterProviderType.Date
  }
}

export class CreatedFilterProvider extends DateFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    const defaultOptions: SuppliedFilterProviderOptions = {filterTypes: {valueless: false}}
    super(FILTER_KEYS.created, FILTER_VALUES.created, {
      ...defaultOptions,
      ...options,
      filterTypes: {...defaultOptions.filterTypes, ...options?.filterTypes},
    })
    this.type = FilterProviderType.Date
  }
}

export class MergedFilterProvider extends DateFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    const opts = {
      ...options,
      filterTypes: {...options?.filterTypes, multiValue: false},
    }
    super(FILTER_KEYS.merged, FILTER_VALUES.merged, opts)
    this.type = FilterProviderType.Date
  }
}

export class UpdatedFilterProvider extends DateFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.updated, FILTER_VALUES.updated, {
      ...options,
      filterTypes: {hasValue: false, ...options?.filterTypes},
    })
    this.type = FilterProviderType.Date
  }
}
